import React from 'react'
import moment from 'moment'
import {
  DialogTitle,
  Dialog,
  DialogContent,
  DialogActions,
  LoadingButton,
  TypoTooltip,
  Box,
  capitalize,
  Row,
  Col,
  Typography,
  CircularProgress,
  Container,
} from '@applift/factor'
import { ChevronDown, ChevronUp } from '@applift/icons'

import {
  CreativeCodeBlock,
  CreativeVisualPreview,
  CreativeDetailsBlock,
} from './components/index'
import { NATIVE_CREATIVE_ID } from '../../constants/creative'
import { useCreativeInfo } from '../../hooks'
import {
  imageDetailsMapper,
  videoDetailsMapper,
  htmlDetailsMapper,
  audioDetailsMapper,
  nativeDetailsMapper,
  nativeVideoDetailsMapper,
  nativeImageDetailsMapper,
} from './helpers'
import { StatusIcon } from '../StatusIcon'
import { creativeIconMap } from '../../constants/campaigns'
import { CreativeInfo } from '../../models/Creative'

import styles from './styles.module.scss'
interface Props {
  creativeId: number
  onClose: () => void
}

export const CreativeDetailDialog = (props: Props) => {
  const { creativeId, onClose } = props

  const creativeInfo = useCreativeInfo(creativeId).data?.[0]

  const [isCodeBlockOpen, setIsCodeBlockOpen] = React.useState(false)
  const [isInfoBlockOpen, setIsInfoBlockOpen] = React.useState(false)
  const [displayShowMore, modifyShowMoreVisibility] =
    React.useState<boolean>(false)
  const [isShowMoreExpanded, setShowMoreExpanded] =
    React.useState<boolean>(false)

  const isNativeVideo =
    creativeInfo?.creativeTypeId === NATIVE_CREATIVE_ID &&
    typeof creativeInfo.duration === 'number'

  const setBlockOpenStatus = (block: string) => (isBlockOpen: boolean) => {
    if (block === 'info') {
      if (isBlockOpen) {
        setIsCodeBlockOpen(false)
      }

      setIsInfoBlockOpen(isBlockOpen)
    } else if (block === 'code') {
      if (isBlockOpen) {
        setIsInfoBlockOpen(false)
      }

      setIsCodeBlockOpen(isBlockOpen)
    }
  }

  React.useEffect(() => {
    if (!isCodeBlockOpen && creativeInfo) {
      setIsInfoBlockOpen(true)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creativeInfo])

  const getCreativeTypeIcon = (creative: CreativeInfo) => {
    const IconComponent =
      creativeIconMap[creative.creativeTypeId as keyof typeof creativeIconMap]
    return <IconComponent sx={{ textColor: 'primary-500', fontSize: 20 }} />
  }

  const detailsMapper = React.useMemo(() => {
    switch (creativeInfo?.creativeType?.toLowerCase()) {
      case 'image':
        return imageDetailsMapper
      case 'video':
        return videoDetailsMapper
      case 'html':
        return htmlDetailsMapper
      case 'audio':
        return audioDetailsMapper
      case 'native':
        return [
          ...nativeDetailsMapper,
          ...(isNativeVideo
            ? nativeVideoDetailsMapper
            : nativeImageDetailsMapper),
        ]
      default:
        return null
    }
  }, [creativeInfo, isNativeVideo])

  const showCodeBlock = React.useMemo(() => {
    const creativeType = creativeInfo?.creativeType?.toLowerCase()
    return (
      creativeType === 'html' ||
      creativeType === 'video' ||
      creativeType === 'audio' ||
      isNativeVideo
    )
  }, [creativeInfo, isNativeVideo])

  const getTitle = (title: string) => (
    <Typography
      variant="bodySmall"
      component="label"
      sx={{
        mr: 2,
        flexShrink: 0,
        textColor: 'neutral-500',
        flexWrap: 'nowrap',
      }}
    >
      {title}
    </Typography>
  )

  const getValue = (val: string | number | JSX.Element) => (
    <TypoTooltip
      placement="top"
      arrow
      TypgraphyProps={{
        gutterBottom: false,
        sx: { display: 'block', mr: 4 },
      }}
    >
      {val}
    </TypoTooltip>
  )

  return (
    <Dialog fullWidth maxWidth="xl" open={!!creativeId}>
      <DialogTitle onClose={onClose}>Creative Preview</DialogTitle>
      <DialogContent
        dividers
        sx={{
          flexDirection: 'column',
          display: 'flex',
          width: 100,
        }}
      >
        {creativeInfo ? (
          <Box sx={{ width: 100 }}>
            <Row>
              <Col xs={12}>
                <TypoTooltip
                  arrow
                  placement="top"
                  title={creativeInfo?.creativeName}
                  TypgraphyProps={{
                    weight: 'demi',
                    variant: 'bodyMedium',
                  }}
                >
                  {`${creativeInfo.creativeId} - ${creativeInfo.creativeName}`}
                </TypoTooltip>
              </Col>
            </Row>

            <Box
              sx={{
                display: 'flex',
                justifyContent: 'between',
                ...(!isShowMoreExpanded ? { position: 'relative' } : {}),
              }}
            >
              <Row
                sx={{
                  width: 100,
                  flexWrap: isShowMoreExpanded ? 'wrap' : 'nowrap',
                  overflowX: 'hidden',
                }}
                xs={{ gutterSize: 4 }}
                ref={rowRef => {
                  // logic to check if the content width overflows container width
                  if ((rowRef?.offsetWidth || 0) < (rowRef?.scrollWidth || 0)) {
                    modifyShowMoreVisibility(true)
                  }
                }}
              >
                <Col className={styles.infoItem} xs="auto">
                  <Box sx={{ display: 'flex' }}>
                    {getTitle('Account:')}
                    {getValue(
                      capitalize(creativeInfo.organizationName as string)
                    )}
                  </Box>
                </Col>

                <Col className={styles.infoItem} xs="auto">
                  <Box sx={{ display: 'flex' }}>
                    {getTitle('Created by:')}
                    {getValue(creativeInfo.userEmail)}
                  </Box>
                </Col>

                <Col className={styles.infoItem} xs="auto">
                  <Box sx={{ display: 'flex' }}>
                    {getTitle('Created on:')}
                    {getValue(
                      moment(creativeInfo.createdOn * 1000).format('MM/DD/YYYY')
                    )}
                  </Box>
                </Col>

                <Col className={styles.infoItem} xs="auto">
                  <Box sx={{ display: 'flex' }}>
                    {getValue(
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          ml: 4,
                          gap: 2,
                        }}
                      >
                        <StatusIcon
                          status={creativeInfo.status}
                          iconProps={{ sx: { fontSize: 16 } }}
                        />
                        {creativeInfo.status === 'approved'
                          ? getValue('Ready')
                          : getValue(capitalize(creativeInfo.status))}
                      </Box>
                    )}
                  </Box>
                </Col>

                <Col className={styles.infoItem} xs="auto">
                  <Box sx={{ display: 'flex' }}>
                    {getValue(
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          ml: 4,
                          gap: 2,
                        }}
                      >
                        {getCreativeTypeIcon(creativeInfo)}
                        {getValue(capitalize(creativeInfo.creativeType))}
                      </Box>
                    )}
                  </Box>
                </Col>
              </Row>
              {displayShowMore && (
                <Box
                  sx={{
                    display: 'flex',
                    flexShrink: 0,
                    alignItems: 'center',
                    px: 8,
                    gap: 4,
                    bgColor: 'neutral-0',
                    height: 100,
                    alignSelf: 'center',
                    ...(!isShowMoreExpanded
                      ? { top: 0, right: 0, position: 'absolute' }
                      : {}),
                  }}
                  className={styles.showMoreContainer}
                  onClick={() => setShowMoreExpanded(!isShowMoreExpanded)}
                >
                  {isShowMoreExpanded ? (
                    <ChevronUp
                      sx={{ textColor: 'primary-500', textWeight: 'demi' }}
                    />
                  ) : (
                    <ChevronDown
                      sx={{ textColor: 'primary-500', textWeight: 'demi' }}
                    />
                  )}
                  <Typography weight="demi" sx={{ textColor: 'primary-500' }}>
                    {isShowMoreExpanded ? 'Show Less' : 'View More'}
                  </Typography>
                </Box>
              )}
            </Box>

            <Row
              sx={{ overflow: 'auto' }}
              // @ts-ignore
              style={{ minHeight: '200px' }}
            >
              <Col xs={6} sx={{ flexGrow: 1, position: 'sticky', top: 0 }}>
                <Box
                  sx={{
                    py: 12,
                  }}
                >
                  <CreativeVisualPreview creativeDetails={creativeInfo} />
                </Box>
              </Col>

              <Col
                xs={6}
                sx={{ flexGrow: 1 }}
                /* @ts-ignore */
                style={{ height: 0 }}
              >
                <Box
                  sx={{
                    gap: 16,
                    display: 'flex',
                    flexDirection: 'column',
                    py: 12,
                  }}
                >
                  {showCodeBlock && (
                    <Box>
                      <CreativeCodeBlock
                        creativeDetails={creativeInfo}
                        isOpen={isCodeBlockOpen}
                        setIsOpen={setBlockOpenStatus('code')}
                      />
                    </Box>
                  )}
                  <Box>
                    <CreativeDetailsBlock
                      creativeDetails={creativeInfo}
                      isOpen={isInfoBlockOpen}
                      setIsOpen={setBlockOpenStatus('info')}
                      infoMapper={detailsMapper}
                    />
                  </Box>
                </Box>
              </Col>
            </Row>
          </Box>
        ) : (
          <Container
            sx={{
              height: 100,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              my: 'auto',
            }}
          >
            <CircularProgress size={64} thickness={2} />
          </Container>
        )}
      </DialogContent>
      <DialogActions>
        <LoadingButton color="primary" onClick={onClose}>
          Done
        </LoadingButton>
      </DialogActions>
    </Dialog>
  )
}
